/*
 * KR Maps Journeys — the app's globe, on the Journeys page hero
 * (sections/kr-journeys-globe.liquid).
 *
 * A port of the app's PrintGlobe (KRMapsJourneys: lib/widgets/print_globe.dart
 * and shaders/print_globe.frag), the welcome screen's version of it. The art
 * is a world print's own zoom-3 tiles, stitched and left in the prints'
 * Patterson projection (the app's tool/globe_art.py); the shader works out
 * where each point of the globe falls on it. The globe turns westward the way
 * the Earth does, pins go in one at a time as their places come round to the
 * front, and one made-up journey draws itself leg by leg in the book studio's
 * ember line, gold to plum. A drag spins it; it coasts, then turns on by
 * itself again.
 *
 * Markup: [data-kr-globe] holding canvas.kr-jg__gl under canvas.kr-jg__pins,
 * with data-art (the print's .webp), data-dark ("1" on a dark ground),
 * data-shade and data-glow (hex). It's square: the globe's radius is its side
 * over 2.7, so the glow round a dark ground's globe reaches its edges. A still
 * of the same globe (img.kr-jg__poster, made from this file at the start
 * position) shows until the live one is ready, and for good where WebGL isn't.
 */
(function () {
  'use strict';

  /* Degrees a second, westward, as the Earth turns. */
  var SPIN = 6;

  /* Radius = side / FRAME. */
  var FRAME = 2.7;

  var PIN_RED = rgb('#DB6035');
  var PAPER = rgb('#FBFAF7');
  var DARK_TEXT = rgb('#F7F2E8');
  var STEEL = rgb('#8E8E8E');
  var STEEL_LIGHT = rgb('#C4C4C4');
  var BLACK = [0, 0, 0];
  var WHITE = [1, 1, 1];

  /* The book studio's route colors, its default "ember" palette. */
  var EMBER = [0xE4AE49, 0xDD8B3B, 0xCE6236, 0xAE3540, 0x7C2E54, 0x43264C];

  /* One journey, westward so each stop comes round just as the line reaches it. */
  var JOURNEY = [
    [41.90, 12.50], // Rome
    [51.51, -0.13], // London
    [64.15, -21.94], // Reykjavik
    [40.71, -74.01], // New York
    [37.77, -122.42], // San Francisco
    [21.31, -157.86], // Honolulu
    [-33.87, 151.21], // Sydney
    [35.01, 135.77], // Kyoto
    [13.76, 100.50], // Bangkok
    [-33.92, 18.42] // Cape Town
  ];

  /* Places pinned on other trips. */
  var OTHERS = [
    [38.72, -9.14], // Lisbon
    [48.86, 2.35], // Paris
    [31.63, -7.99], // Marrakesh
    [41.01, 28.98], // Istanbul
    [-13.53, -71.97], // Cusco
    [19.43, -99.13], // Mexico City
    [51.18, -115.57], // Banff
    [-8.41, 115.19], // Bali
    [-45.03, 168.66] // Queenstown
  ];

  /* ── Colors ─────────────────────────────────────────────────────────── */

  function rgb(hex) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) n = 0;
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function css(c, a) {
    return 'rgba(' + Math.round(c[0] * 255) + ',' + Math.round(c[1] * 255) + ',' + Math.round(c[2] * 255) + ',' + Math.max(0, Math.min(1, a)).toFixed(3) + ')';
  }

  /* The palette at t from 0 (gold) to 1 (plum), read in OKLab: the app's emberAt. */
  function emberAt(t) {
    t = Math.max(0, Math.min(1, t));
    var pos = t * (EMBER.length - 1);
    var i = Math.min(Math.floor(pos), EMBER.length - 2);
    var f = pos - i;
    var a = toLab(EMBER[i]), b = toLab(EMBER[i + 1]);
    return toRgb([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]);
  }

  function toLinear(c) {
    var v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }

  function toGamma(v) {
    var c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(1, c));
  }

  function cbrt(x) {
    return x < 0 ? -Math.pow(-x, 1 / 3) : Math.pow(x, 1 / 3);
  }

  function toLab(hex) {
    var r = toLinear((hex >> 16) & 255), g = toLinear((hex >> 8) & 255), b = toLinear(hex & 255);
    var l = cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    var m = cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    var s = cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
      0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
      1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
      0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s
    ];
  }

  function toRgb(lab) {
    var l = Math.pow(lab[0] + 0.3963377774 * lab[1] + 0.2158037573 * lab[2], 3);
    var m = Math.pow(lab[0] - 0.1055613458 * lab[1] - 0.0638541728 * lab[2], 3);
    var s = Math.pow(lab[0] - 0.0894841775 * lab[1] - 1.2914855480 * lab[2], 3);
    return [
      Math.round(toGamma(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s) * 255) / 255,
      Math.round(toGamma(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s) * 255) / 255,
      Math.round(toGamma(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s) * 255) / 255
    ];
  }

  /* Flutter's Curves.easeInOutCubic, which is Cubic(0.645, 0.045, 0.355, 1.0). */
  function easeInOutCubic(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    var start = 0, end = 1;
    for (var i = 0; i < 40; i++) {
      var mid = (start + end) / 2;
      var estimate = cubic(0.645, 0.355, mid);
      if (Math.abs(t - estimate) < 0.001) return cubic(0.045, 1.0, mid);
      if (estimate < t) start = mid; else end = mid;
    }
    return cubic(0.045, 1.0, (start + end) / 2);
  }

  function cubic(a, b, m) {
    return 3 * a * (1 - m) * (1 - m) * m + 3 * b * (1 - m) * m * m + m * m * m;
  }

  /* ── The globe: where it's turned to, what's pinned, how far the lines are ── */

  function vec(lat, lon) {
    var la = lat * Math.PI / 180, lo = lon * Math.PI / 180;
    return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
  }

  /* A line between two pins along the great circle, lifted a little off the
     globe, the more the longer it is, so it arcs like a flight. */
  function Leg(a, b, from, to, rise) {
    var d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    this.angle = Math.acos(d);
    this.from = from;
    this.to = to;
    this.points = [];
    this.lift = [];
    this.startAt = null;
    this.seconds = 1.0 + 1.7 * this.angle / Math.PI;
    var steps = Math.max(16, Math.ceil(this.angle / 0.025));
    var height = rise + 0.10 * this.angle / Math.PI;
    var sa = Math.sin(this.angle);
    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      if (this.angle < 1e-6) {
        this.points.push(a);
      } else {
        var s1 = Math.sin((1 - t) * this.angle) / sa, s2 = Math.sin(t * this.angle) / sa;
        this.points.push([s1 * a[0] + s2 * b[0], s1 * a[1] + s2 * b[1], s1 * a[2] + s2 * b[2]]);
      }
      this.lift.push(1 + height * Math.sin(Math.PI * t));
    }
  }

  function Globe() {
    var stops = JOURNEY.map(function (p, k) {
      return { w: vec(p[0], p[1]), color: PIN_RED, stop: k, placedAt: null };
    });
    var others = OTHERS.map(function (p) {
      return { w: vec(p[0], p[1]), color: PIN_RED, stop: -1, placedAt: null };
    });
    this.pins = stops.concat(others);
    this.legs = [];
    var n = stops.length - 1;
    for (var k = 0; k < n; k++) {
      this.legs.push(new Leg(stops[k].w, stops[k + 1].w, emberAt(k / n), emberAt((k + 1) / n), 0.03));
    }
    this.lon0 = -15;
    this.restTilt = 0.37;
    this.tilt = this.restTilt;
    this.velocity = 0;
    this.dragging = false;
    this.idle = 99;
    this.fade = 0;
    this.reduceMotion = false;
    this.now = 0;
    this.readyAt = null;
    this.lastPin = -1;
    this.gap = 0.23;
  }

  /* World to view: x right, y up, z toward the viewer. */
  Globe.prototype.view = function (w) {
    var l = this.lon0 * Math.PI / 180;
    var cl = Math.cos(l), sl = Math.sin(l), ct = Math.cos(this.tilt), st = Math.sin(this.tilt);
    var a = w[0] * cl + w[1] * sl;
    return [w[1] * cl - w[0] * sl, ct * w[2] - st * a, st * w[2] + ct * a];
  };

  /* A frame. False once nothing will move until the globe is touched. */
  Globe.prototype.advance = function (dt) {
    this.now += dt;
    if (this.readyAt === null) return true;
    if (!this.dragging) {
      if (Math.abs(this.velocity) > 1) {
        this.lon0 += this.velocity * dt;
        this.velocity *= Math.pow(0.03, dt);
        this.idle = 0;
      } else {
        this.velocity = 0;
        this.idle += dt;
      }
      if (!this.reduceMotion && this.idle > 1.2) this.lon0 -= SPIN * Math.min(1, (this.idle - 1.2) / 1.5) * dt;
      this.tilt += (this.restTilt - this.tilt) * (1 - Math.pow(0.4, dt));
    }
    this.lon0 = ((this.lon0 % 360) + 540) % 360 - 180;
    this.fade = this.reduceMotion ? 1 : Math.min(1, this.fade + dt / 0.8);
    this.placePins();
    this.startLegs();
    if (!this.reduceMotion || this.dragging || this.velocity !== 0) return true;
    // Reduced motion: still once it's in and the globe has settled.
    return Math.abs(this.tilt - this.restTilt) > 0.001;
  };

  Globe.prototype.grab = function () {
    this.dragging = true;
    this.velocity = 0;
    this.idle = 0;
  };

  Globe.prototype.drag = function (dx, dy, radius) {
    var k = 180 / Math.PI / Math.max(40, radius);
    this.lon0 -= dx * k;
    this.tilt = Math.max(-1, Math.min(1, this.tilt + dy / Math.max(40, radius)));
    this.idle = 0;
  };

  /* vx in px a second. */
  Globe.prototype.release = function (vx, radius) {
    this.dragging = false;
    this.idle = 0;
    this.velocity = Math.max(-400, Math.min(400, -vx * 180 / Math.PI / Math.max(40, radius)));
  };

  /* Pins go in one at a time as their places come round to the front: the
     journey's in its order, then the rest. */
  Globe.prototype.placePins = function () {
    var pins = this.pins, i, p;
    if (this.reduceMotion) {
      for (i = 0; i < pins.length; i++) if (pins[i].placedAt === null) pins[i].placedAt = -10;
      return;
    }
    if (this.now - this.readyAt < 0.8 || this.now - this.lastPin < this.gap) return;
    var pick = null, best = -Infinity;
    for (i = 0; i < pins.length; i++) {
      p = pins[i];
      if (p.placedAt !== null) continue;
      var z = this.view(p.w)[2];
      if (z < 0.3) continue;
      var score = p.stop >= 0 ? 100 - p.stop : z;
      if (score > best) {
        best = score;
        pick = p;
      }
    }
    if (!pick) return;
    pick.placedAt = this.now;
    this.lastPin = this.now;
  };

  /* The journey's legs one after another, once the pins at both ends are in. */
  Globe.prototype.startLegs = function () {
    for (var k = 0; k < this.legs.length; k++) {
      var leg = this.legs[k];
      if (leg.startAt !== null) continue;
      var a = this.pins[k].placedAt, b = this.pins[k + 1].placedAt;
      if (a === null || b === null) return;
      if (this.reduceMotion) {
        leg.startAt = -10;
        continue;
      }
      var before = k === 0 ? 0 : this.legs[k - 1].startAt + this.legs[k - 1].seconds;
      leg.startAt = Math.max(this.now, before, Math.max(a, b) + 0.35);
      return;
    }
  };

  /* ── The print on the globe: WebGL, shaders/print_globe.frag ─────────── */

  var VERTEX = 'attribute vec2 aPos; void main() { gl_Position = vec4(aPos, 0.0, 1.0); }';

  var FRAGMENT = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform vec2 uCenter;', // the globe's center, css px, y up
    'uniform float uRadius;', // css px
    'uniform float uPixel;', // one device pixel, in css px
    'uniform float uLon0;', // the longitude facing the viewer, radians
    'uniform float uTilt;', // north tipped toward the viewer, radians
    'uniform float uFade;',
    'uniform float uDark;', // 1 on a dark ground, 0 on a light one
    'uniform vec3 uShade;', // the ground's deepest tone, which the rim darkens toward
    'uniform vec3 uGlow;', // the glow outside the rim, on a dark ground
    'uniform sampler2D uArt;',
    'const float PI = 3.14159265;',
    'const float Y_MAX = 1.7908608;',
    'const vec3 LIGHT = vec3(-0.451, 0.551, 0.702);',
    // Patterson cylindrical's y for a latitude.
    'float pattersonY(float phi) {',
    '  float p2 = phi * phi;',
    '  float p4 = p2 * p2;',
    '  return phi * (1.0148 + 0.23185 * p4 - 0.14499 * p4 * p2 + 0.02406 * p4 * p4);',
    '}',
    // The print under a point of the globe's disc (radius 1, y up).
    'vec3 art(vec2 p) {',
    '  float z = sqrt(max(0.0, 1.0 - dot(p, p)));',
    '  float ct = cos(uTilt);',
    '  float st = sin(uTilt);',
    '  float lat = asin(clamp(ct * p.y + st * z, -1.0, 1.0));',
    '  float lon = atan(p.x, ct * z - st * p.y) + uLon0;',
    '  vec2 uv = vec2(fract(lon / (2.0 * PI) + 0.5), (Y_MAX - pattersonY(lat)) / (2.0 * Y_MAX));',
    '  return texture2D(uArt, uv).rgb;',
    '}',
    'void main() {',
    '  vec2 p = (gl_FragCoord.xy * uPixel - uCenter) / uRadius;',
    '  float r = length(p);',
    '  float px = uPixel / uRadius;',
    // On a dark ground, a glow just outside the rim.
    '  vec4 glow = vec4(0.0);',
    '  if (uDark > 0.5 && r > 1.0) {',
    '    float d = (r - 1.0) / 0.14;',
    '    float a = 0.10 * exp(-d * d);',
    '    glow = vec4(uGlow * a, a);',
    '  }',
    '  float edge = clamp((1.0 - r) / px + 0.5, 0.0, 1.0);',
    '  if (edge <= 0.0) {',
    '    gl_FragColor = glow * uFade;',
    '    return;',
    '  }',
    // Four looks a pixel, so the print's fine lines don't shimmer as they turn toward the rim.
    '  vec3 col = 0.25 * (art(p + vec2(0.125, 0.375) * px) + art(p + vec2(-0.375, 0.125) * px) +',
    '                     art(p + vec2(0.375, -0.125) * px) + art(p + vec2(-0.125, -0.375) * px));',
    // Lit from the upper left, darkening toward the rim, with a little gloss like the laminate on a print.
    '  float z = sqrt(max(0.0, 1.0 - r * r));',
    '  vec3 n = vec3(p, z);',
    '  col *= mix(0.82, 0.80, uDark) + mix(0.26, 0.32, uDark) * max(dot(n, LIGHT), 0.0);',
    '  col = mix(col, uShade, mix(0.34, 0.55, uDark) * pow(1.0 - z, 2.4));',
    '  vec3 h = normalize(LIGHT + vec3(0.0, 0.0, 1.0));',
    '  col += vec3(mix(0.11, 0.13, uDark) * pow(max(dot(n, h), 0.0), 36.0));',
    // A hairline round the rim.
    '  float ring = clamp(1.0 - (1.0 - r) * uRadius / 1.3, 0.0, 1.0);',
    '  vec4 rim = mix(vec4(0.13, 0.11, 0.09, 0.2), vec4(0.97, 0.95, 0.91, 0.16), uDark);',
    '  col = mix(col, rim.rgb, rim.a * ring);',
    '  vec4 globe = vec4(clamp(col, 0.0, 1.0) * edge, edge);',
    '  gl_FragColor = (globe + glow * (1.0 - edge)) * uFade;',
    '}'
  ].join('\n');

  function Painter(canvas) {
    var gl = null;
    try {
      var opts = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false };
      gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts);
    } catch (e) {
      gl = null;
    }
    if (!gl) throw new Error('no WebGL');
    this.gl = gl;
    var program = gl.createProgram();
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
    gl.bindAttribLocation(program, 0, 'aPos');
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'link failed');
    gl.useProgram(program);
    var names = ['uCenter', 'uRadius', 'uPixel', 'uLon0', 'uTilt', 'uFade', 'uDark', 'uShade', 'uGlow', 'uArt'];
    this.u = {};
    for (var i = 0; i < names.length; i++) this.u[names[i]] = gl.getUniformLocation(program, names[i]);
    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    this.texture = null;
  }

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || 'compile failed');
    return shader;
  }

  /* Bilinear, no mipmaps: the longitude wrap would show a seam, as in the app. */
  Painter.prototype.wear = function (image) {
    var gl = this.gl;
    // Some old phones' GPUs take nothing wider than 2048 px; they get the
    // print scaled to fit rather than a black globe.
    var source = image;
    var max = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048;
    var iw = image.naturalWidth || image.width, ih = image.naturalHeight || image.height;
    if (iw > max || ih > max) {
      var k = Math.min(max / iw, max / ih);
      source = document.createElement('canvas');
      source.width = Math.floor(iw * k);
      source.height = Math.floor(ih * k);
      source.getContext('2d').drawImage(image, 0, 0, source.width, source.height);
    }
    var texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
    if (gl.getError() !== gl.NO_ERROR) {
      gl.deleteTexture(texture);
      throw new Error('the print would not go on');
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (this.texture) gl.deleteTexture(this.texture);
    this.texture = texture;
    gl.uniform1i(this.u.uArt, 0);
  };

  /* w, h in css px; the canvas is w * dpr by h * dpr. */
  Painter.prototype.paint = function (globe, ground, w, h, dpr) {
    var gl = this.gl, u = this.u, r = Math.min(w, h) / FRAME;
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (!this.texture || globe.fade <= 0) return;
    gl.uniform2f(u.uCenter, w / 2, h / 2);
    gl.uniform1f(u.uRadius, r);
    gl.uniform1f(u.uPixel, 1 / dpr);
    gl.uniform1f(u.uLon0, globe.lon0 * Math.PI / 180);
    gl.uniform1f(u.uTilt, globe.tilt);
    gl.uniform1f(u.uFade, globe.fade);
    gl.uniform1f(u.uDark, ground.dark ? 1 : 0);
    gl.uniform3f(u.uShade, ground.shade[0], ground.shade[1], ground.shade[2]);
    gl.uniform3f(u.uGlow, ground.glow[0], ground.glow[1], ground.glow[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  };

  Painter.prototype.dispose = function () {
    var gl = this.gl;
    if (this.texture) gl.deleteTexture(this.texture);
    var lose = gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
  };

  /* ── Pins and the journey, over the print: the app's _GlobePainter ───── */

  /* Where a leg runs, as the stretches of it in front of the globe or out
     past its rim, up to `upto` of its points; and where it has got to, when
     that's in sight. */
  function runsOf(globe, leg, cx, cy, r, upto) {
    var runs = [], run = null, head = null;
    function add(w, h) {
      var v = globe.view(w);
      var x = v[0] * h, y = v[1] * h, z = v[2] * h;
      if (z <= 0 && x * x + y * y <= 1) {
        run = null;
        return null;
      }
      var at = [cx + r * x, cy - r * y];
      if (run === null) {
        run = [];
        runs.push(run);
      }
      run.push(at);
      return at;
    }
    var last = Math.floor(upto);
    for (var i = 0; i <= last; i++) head = add(leg.points[i], leg.lift[i]);
    if (last < leg.points.length - 1) {
      var f = upto - last, a = leg.points[last], b = leg.points[last + 1];
      head = add([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f],
        leg.lift[last] + (leg.lift[last + 1] - leg.lift[last]) * f);
    }
    return { runs: runs, head: head };
  }

  function trace(ctx, runs) {
    ctx.beginPath();
    for (var i = 0; i < runs.length; i++) {
      var run = runs[i];
      if (run.length < 2) continue;
      ctx.moveTo(run[0][0], run[0][1]);
      for (var j = 1; j < run.length; j++) ctx.lineTo(run[j][0], run[j][1]);
    }
  }

  /* The journey, drawing itself leg by leg in the studio's line: a paper halo
     under a thinner core, gold to plum. */
  function paintJourney(ctx, globe, cx, cy, r, fade, dark) {
    var drawn = [], i;
    for (i = 0; i < globe.legs.length; i++) {
      var leg = globe.legs[i];
      if (leg.startAt === null) continue;
      var p = (globe.now - leg.startAt) / leg.seconds;
      if (p <= 0) continue;
      var n = leg.points.length - 1;
      var upto = easeInOutCubic(Math.min(1, p)) * n;
      var got = runsOf(globe, leg, cx, cy, r, upto);
      drawn.push([leg, got.runs, upto < n ? got.head : null]);
    }
    if (!drawn.length) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 5;
    ctx.strokeStyle = css(dark ? DARK_TEXT : PAPER, (dark ? 0.5 : 0.82) * fade);
    for (i = 0; i < drawn.length; i++) {
      trace(ctx, drawn[i][1]);
      ctx.stroke();
    }
    for (i = 0; i < drawn.length; i++) {
      var l = drawn[i][0], runs = drawn[i][1], tip = drawn[i][2];
      if (!runs.length) continue;
      var a = globe.view(l.points[0]), b = globe.view(l.points[l.points.length - 1]);
      var fx = cx + r * a[0], fy = cy - r * a[1], tx = cx + r * b[0], ty = cy - r * b[1];
      ctx.lineWidth = 2.2;
      if (Math.hypot(tx - fx, ty - fy) > 1) {
        var g = ctx.createLinearGradient(fx, fy, tx, ty);
        g.addColorStop(0, css(l.from, fade));
        g.addColorStop(1, css(l.to, fade));
        ctx.strokeStyle = g;
      } else {
        ctx.strokeStyle = css(l.from, fade);
      }
      trace(ctx, runs);
      ctx.stroke();
      if (tip) {
        ctx.beginPath();
        ctx.arc(tip[0], tip[1], 3.2, 0, 2 * Math.PI);
        ctx.fillStyle = css(PAPER, fade);
        ctx.fill();
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = css(l.to, fade);
        ctx.stroke();
      }
    }
  }

  function paintPins(ctx, globe, cx, cy, r, fade) {
    var shown = [], i;
    for (i = 0; i < globe.pins.length; i++) {
      var p = globe.pins[i];
      if (p.placedAt === null) continue;
      var v = globe.view(p.w);
      if (v[2] > 0) shown.push([v, p]);
    }
    // Back to front.
    shown.sort(function (a, b) { return a[0][2] - b[0][2]; });
    // A soft shadow each, unless there are so many that plain ones do.
    var soft = shown.length <= 120;
    for (i = 0; i < shown.length; i++) {
      var sv = shown[i][0], sp = shown[i][1];
      var age = globe.now - sp.placedAt;
      var e = age >= 0.6 ? 1 : 1 - Math.pow(1 - Math.max(0, age) / 0.6, 3);
      var depth = Math.min(1, sv[2] / 0.22);
      var alpha = Math.min(1, age / 0.18) * depth * fade;
      if (alpha <= 0.01) continue;
      paintPin(ctx, cx + r * sv[0], cy - r * sv[1], sv, sp.color, 1 + 1.3 * (1 - e), 1 + 0.3 * (1 - e), alpha, age, soft);
    }
  }

  /* The app's push pin, stuck in along the globe's surface. It comes down from
     above and is pressed in, leaving a ring where it went in. */
  function paintPin(ctx, bx, by, v, color, reach, scale, alpha, ring, soft) {
    var z = Math.max(0.06, v[2]);
    var length = 13 * reach;
    var hx = bx + length * v[0], hy = by - length * v[1];
    var radius = 6.2 * scale;
    var angle = Math.atan2(-v[1], v[0]);

    if (ring > 0.3 && ring < 1.3) {
      var t = ring - 0.3, spread = 4 + 15 * (1 - (1 - t) * (1 - t));
      ctx.save();
      ctx.translate(bx, by);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.ellipse(0, 0, spread * z, spread, 0, 0, 2 * Math.PI);
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = css(color, alpha * 0.55 * (1 - t));
      ctx.stroke();
      ctx.restore();
    }

    // Where it meets the globe.
    ctx.save();
    ctx.translate(bx, by);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.ellipse(0, 0, 3.4 * z + 0.4, 3.4, 0, 0, 2 * Math.PI);
    ctx.fillStyle = css(BLACK, 0.28 * alpha);
    ctx.fill();
    ctx.restore();
    var sx = bx + (hx - bx) * 0.3 + 2.6, sy = by + (hy - by) * 0.3 + 3.4;
    ctx.beginPath();
    if (soft) {
      var g = ctx.createRadialGradient(sx, sy, 0, sx, sy, radius * 1.7);
      g.addColorStop(0, css(BLACK, 0.2 * alpha));
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.arc(sx, sy, radius * 1.7, 0, 2 * Math.PI);
    } else {
      ctx.fillStyle = css(BLACK, 0.12 * alpha);
      ctx.arc(sx, sy, radius * 1.1, 0, 2 * Math.PI);
    }
    ctx.fill();

    // The steel stem, then the head: a dark rim, the color, a highlight.
    ctx.lineCap = 'round';
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = css(STEEL, alpha);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    ctx.lineWidth = 0.9;
    ctx.strokeStyle = css(STEEL_LIGHT, alpha);
    ctx.beginPath();
    ctx.moveTo(bx - 0.5, by);
    ctx.lineTo(hx - 0.5, hy);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(hx, hy, radius + 0.9, 0, 2 * Math.PI);
    ctx.fillStyle = css(BLACK, 0.35 * alpha);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(hx, hy, radius, 0, 2 * Math.PI);
    ctx.fillStyle = css(color, alpha);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(hx, hy, radius * 0.62, -2.6, -2.6 + 1.1);
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = css(WHITE, 0.45 * alpha);
    ctx.stroke();
  }

  /* ── One globe on the page ───────────────────────────────────────────── */

  function mount(stage) {
    if (stage.__krGlobe) return stage.__krGlobe;
    var glCanvas = stage.querySelector('.kr-jg__gl');
    var pinCanvas = stage.querySelector('.kr-jg__pins');
    var poster = stage.querySelector('.kr-jg__poster');
    var art = stage.getAttribute('data-art');
    if (!glCanvas || !pinCanvas || !art) return null;
    // A still frame for the poster: the globe at its start, no pins.
    var still = stage.hasAttribute('data-still');

    var ground = {
      dark: stage.getAttribute('data-dark') === '1',
      shade: rgb(stage.getAttribute('data-shade') || '#0f0b08'),
      glow: rgb(stage.getAttribute('data-glow') || '#d9b58a')
    };
    var globe = new Globe();
    var painter;
    try {
      painter = new Painter(glCanvas);
    } catch (e) {
      // No globe then; the still stays, and the words and buttons beside it work.
      stage.classList.add('is-still');
      return null;
    }
    var ctx = pinCanvas.getContext('2d');
    var w = 0, h = 0, dpr = 1, raf = 0, last = null, visible = true, ready = false, dead = false;
    var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    globe.reduceMotion = !!(motion && motion.matches);

    function measure() {
      var box = stage.getBoundingClientRect();
      w = box.width;
      h = box.height;
      // Two device pixels a css pixel are plenty for four looks a pixel.
      dpr = Math.min(2, window.devicePixelRatio || 1);
      var cw = Math.max(1, Math.round(w * dpr)), ch = Math.max(1, Math.round(h * dpr));
      if (glCanvas.width !== cw || glCanvas.height !== ch) {
        glCanvas.width = cw;
        glCanvas.height = ch;
      }
      if (pinCanvas.width !== cw || pinCanvas.height !== ch) {
        pinCanvas.width = cw;
        pinCanvas.height = ch;
      }
    }

    function paint() {
      if (!ready || !w || !h) return;
      painter.paint(globe, ground, w, h, dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, pinCanvas.width, pinCanvas.height);
      if (still) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var r = Math.min(w, h) / FRAME;
      paintJourney(ctx, globe, w / 2, h / 2, r, globe.fade, ground.dark);
      paintPins(ctx, globe, w / 2, h / 2, r, globe.fade);
    }

    function frame(t) {
      raf = 0;
      var dt = last === null ? 0 : Math.max(0, Math.min(0.05, (t - last) / 1000));
      last = t;
      var going = still ? false : globe.advance(dt);
      paint();
      if (going && visible && !dead) raf = requestAnimationFrame(frame);
      else last = null;
    }

    function wake() {
      if (!raf && visible && !dead) raf = requestAnimationFrame(frame);
    }

    // Its print. Until it's in, the still shows.
    var image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = function () {
      if (dead) return;
      try {
        painter.wear(image);
      } catch (e) {
        stage.classList.add('is-still');
        return;
      }
      measure();
      ready = true;
      globe.readyAt = globe.now;
      // The still is this globe at this moment, so it takes over without the
      // app's fade; without a still it fades in as the app's does.
      var posterShowing = poster && poster.complete && poster.naturalWidth > 0;
      if (posterShowing || still) globe.fade = 1;
      paint();
      stage.classList.add('is-live');
      if (still) stage.setAttribute('data-painted', '1');
      wake();
    };
    image.onerror = function () {
      stage.classList.add('is-still');
    };
    image.src = art;

    // A drag spins it: sideways turns it, up and down tips it. Touch leaves
    // up and down to the page (touch-action: pan-y), so it never traps a scroll.
    var pointer = null, lastX = 0, lastY = 0, samples = [];
    function onDown(e) {
      if (!ready || still || pointer !== null) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointer = e.pointerId;
      try { stage.setPointerCapture(pointer); } catch (err) { /* already gone */ }
      lastX = e.clientX;
      lastY = e.clientY;
      samples = [[e.timeStamp, e.clientX]];
      globe.grab();
      stage.classList.add('is-grabbing');
      wake();
    }
    function onMove(e) {
      if (e.pointerId !== pointer) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      globe.drag(dx, dy, Math.min(w, h) / FRAME);
      samples.push([e.timeStamp, e.clientX]);
      while (samples.length > 2 && e.timeStamp - samples[0][0] > 100) samples.shift();
      wake();
    }
    function onUp(e) {
      if (e.pointerId !== pointer) return;
      pointer = null;
      stage.classList.remove('is-grabbing');
      var vx = 0;
      if (e.type === 'pointerup' && samples.length > 1) {
        var first = samples[0], end = samples[samples.length - 1];
        // As Flutter's tracker: a pointer held still before it lifts has stopped.
        if (e.timeStamp - end[0] <= 40 && end[0] - first[0] > 0) vx = (end[1] - first[1]) / ((end[0] - first[0]) / 1000);
      }
      globe.release(vx, Math.min(w, h) / FRAME);
      wake();
    }
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('lostpointercapture', onUp);

    var resize = typeof ResizeObserver === 'function' ? new ResizeObserver(function () {
      measure();
      paint();
    }) : null;
    if (resize) resize.observe(stage);
    else window.addEventListener('resize', measure);
    measure();

    // Only turning while it can be seen.
    var seen = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
      visible = entries[entries.length - 1].isIntersecting;
      if (visible) wake();
    }) : null;
    if (seen) seen.observe(stage);

    function onMotion() {
      globe.reduceMotion = !!(motion && motion.matches);
      wake();
    }
    if (motion) {
      if (motion.addEventListener) motion.addEventListener('change', onMotion);
      else if (motion.addListener) motion.addListener(onMotion);
    }

    glCanvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      dead = true;
      stage.classList.remove('is-live');
      stage.classList.add('is-still');
    });

    var handle = {
      // What it's showing, for a look from the console.
      globe: globe,
      destroy: function () {
        dead = true;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        if (resize) resize.disconnect();
        if (seen) seen.disconnect();
        if (motion) {
          if (motion.removeEventListener) motion.removeEventListener('change', onMotion);
          else if (motion.removeListener) motion.removeListener(onMotion);
        }
        stage.removeEventListener('pointerdown', onDown);
        stage.removeEventListener('pointermove', onMove);
        stage.removeEventListener('pointerup', onUp);
        stage.removeEventListener('pointercancel', onUp);
        stage.removeEventListener('lostpointercapture', onUp);
        painter.dispose();
        delete stage.__krGlobe;
      }
    };
    stage.__krGlobe = handle;
    return handle;
  }

  function mountAll(root) {
    var stages = (root || document).querySelectorAll('[data-kr-globe]');
    for (var i = 0; i < stages.length; i++) mount(stages[i]);
  }

  // The theme editor reloads a section in place.
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var stages = e.target.querySelectorAll('[data-kr-globe]');
    for (var i = 0; i < stages.length; i++) if (stages[i].__krGlobe) stages[i].__krGlobe.destroy();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(); });
  else mountAll();
})();
